import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiInbox,
  FiFileText,
  FiShoppingCart,
  FiList,
  FiSettings,
  FiBox,
  FiLayers,
  FiUsers,
  FiBarChart,
  FiChevronDown,
} from 'react-icons/fi';
import { X as CloseIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { hasScreenPermission } from '../lib/permissions';

/* ----------------------------------------------------------
   CUSTOM ICONS (Feather-ish inline SVG)
---------------------------------------------------------- */
const ArrowLeftIcon = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ArrowRightIcon = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// Vendors icon
const TruckIcon = ({
  size = 18,
  className = '',
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="1.5" />
    <circle cx="18.5" cy="18.5" r="1.5" />
  </svg>
);

// Confirmed Order icon
const CheckBadgeIcon = ({
  size = 18,
  className = '',
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.2 2.2 4.8-5.2" />
  </svg>
);

const CurrencyIcon = ({
  size = 18,
  className = '',
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="3" />
    <path d="M6 9v.01M18 15v.01" />
  </svg>
);

const CreditNoteIcon = ({
  size = 18,
  className = '',
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12h8" />
  </svg>
);

/* ----------------------------------------------------------
   MENU CONFIGURATION
---------------------------------------------------------- */
const menu = [
  { key: 'home', label: 'Home', icon: FiHome, permissionScreen: 'dashboard', path: '/app' },
  { key: 'rfq-inbox', label: 'Inbox', icon: FiInbox, permissionScreen: 'quotations', path: '/rfq' },
  { key: 'quote-generator', label: 'Quote Generator', icon: FiFileText, permissionScreen: 'quotations', path: '/quotations' },
  { key: 'confirmed-order', label: 'Confirmed Order', icon: CheckBadgeIcon, permissionScreen: 'confirmed_orders', path: '/confirmed-orders' },
  { key: 'vendor-purchase-order', label: 'Vendor Purchase Order', icon: FiShoppingCart, permissionScreen: 'vendor_purchase_orders', path: '/vendor-purchase-orders' },

  { key: 'invoice', label: 'Client Invoices', icon: CurrencyIcon, permissionScreen: 'invoices', path: '/client-invoices' },
  { key: 'credit-notes', label: 'Credit Notes', icon: CreditNoteIcon, permissionScreen: 'invoices', path: '/credit-notes' },

  // Vendor Invoices
  {
    key: 'vendor-invoices',
    label: 'Vendor Invoices',
    icon: CurrencyIcon,
    permissionScreen: 'invoices',
    path: '/vendor-invoices',
  },

  { key: 'client-price-list', label: 'Client Price List', icon: FiList, permissionScreen: 'products', path: '/client-price-lists' },
  { key: 'data-sheets', label: 'Product Data Sheets', icon: FiFileText, permissionScreen: 'products', path: '/data-sheets' },
  { key: 'master-catalog', label: 'Master Catalog', icon: FiLayers, permissionScreen: 'products', path: '/master-catalogue' },
  { key: 'inventory', label: 'Inventory Management', icon: FiBox, permissionScreen: 'inventory', path: '/inventory' },
  { key: 'accounts', label: 'Accounts', icon: CurrencyIcon, permissionScreen: 'financials', path: '/accounts' },
  { key: 'reports', label: 'Reports', icon: FiBarChart, permissionScreen: 'reports', path: '/reports' },

  // Admin
  { key: 'users', label: 'User Management', icon: FiUsers, permissionScreen: 'users', path: '/users' },
  { key: 'templates', label: 'Template Management', icon: FiFileText, permissionScreen: 'templates', path: '/templates' },
  { key: 'vendors', label: 'Vendor List', icon: TruckIcon, permissionScreen: 'vendors', path: '/vendors' },

  // ✅ Client List (Admin Settings -> below Vendor List)
  { key: 'clients', label: 'Client List', icon: FiUsers, permissionScreen: 'clients', path: '/clients' },

  { key: 'settings', label: 'Settings', icon: FiSettings, permissionScreen: 'settings', path: '/settings' },
];

// The only modules currently exposed in the UI. Everything else stays fully built — backend
// routes, services and jobs are untouched — it is just not navigable. Intersected into the
// existing permission filter, so a user still never sees something their role forbids.
//
// The matching route-level block lives in App.tsx (ENABLED_PATH_PREFIXES); this Set only
// controls the sidebar. Both are needed: hiding a link does not stop a typed URL. Re-enabling
// a module means adding its key here AND its path prefix there.
//
// (A previous demo-scoping pass used the same one-Set approach and was reverted cleanly in
// 9de2afb — keep it equally easy to undo.)
// Nav is an explicit allowlist, not everything defined in MENU — a key missing from here
// is silently hidden no matter what permissions the user has.
const ENABLED_KEYS = new Set([
  'rfq-inbox',
  'quote-generator',
  'clients',
  'vendors',
  'master-catalog',
  'data-sheets',
]);

interface SidebarProps {
  activeKey?: string;
  onChange?: (key: string) => void;
  /** Mobile drawer open state — the sidebar is an off-canvas overlay below the md
   * breakpoint, controlled by MainLayout's hamburger button in TopBar. */
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ activeKey, onChange, mobileOpen = false, onCloseMobile }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [adminOpen, setAdminOpen] = useState(true);

  const visibleMenuItems = useMemo(
    () =>
      menu.filter(
        (item) => ENABLED_KEYS.has(item.key) && hasScreenPermission(user, item.permissionScreen, 'view'),
      ),
    [user]
  );

  const getCurrentKey = () => {
    if (activeKey) return activeKey;
    const exact = menu.find((i) => i.path === location.pathname);
    if (exact) return exact.key;
    const prefix = menu.find((i) => location.pathname.startsWith(i.path + '/'));
    return prefix?.key || 'home';
  };

  const currentActiveKey = getCurrentKey();

  const handleItemClick = (item: typeof menu[0]) => {
    onChange?.(item.key);
    navigate(item.path);
    onCloseMobile?.();
  };

  // Icon-only collapse is a desktop preference — the mobile drawer always shows full
  // labels since it's opened deliberately and closes itself after a tap.
  const iconOnly = collapsed && !mobileOpen;

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const mainKeys = new Set([
    'home',
    'rfq-inbox',
    'quote-generator',
    'confirmed-order',
    'vendor-purchase-order',
    'invoice',
    'credit-notes',
    'vendor-invoices',
    'client-price-list',
    'master-catalog',
    'inventory',
    'accounts',
    'reports',
  ]);

  // ✅ Add "clients" under Admin Settings
  const adminKeys = new Set(['users', 'templates', 'vendors', 'clients', 'settings']);

  const mainItems = visibleMenuItems.filter((i) => mainKeys.has(i.key));
  const adminItems = visibleMenuItems.filter((i) => adminKeys.has(i.key));

  return (
    <>
      {/* Mobile backdrop — tapping outside the drawer closes it */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}
      <aside
        className={[
          'bg-white border-r border-slate-200 flex flex-col no-print min-w-0 overflow-x-hidden',
          // Mobile: fixed off-canvas drawer, slides in over the content.
          'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop (md+): back to a normal, always-visible flex column at its own width.
          'md:static md:translate-x-0 md:z-auto',
          iconOnly ? 'md:w-16' : 'md:w-60',
        ].join(' ')}
      >
      <div className="flex items-center justify-between px-3 py-3 border-b border-slate-200 min-w-0">
        {!iconOnly && (
          <div className="text-sm font-semibold text-slate-700 truncate min-w-0">
            Aquamaris Trading
          </div>
        )}
        <button
          onClick={toggleCollapsed}
          className="hidden md:inline-flex text-slate-600 hover:text-slate-900 shrink-0"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ArrowRightIcon /> : <ArrowLeftIcon />}
        </button>
        <button
          onClick={onCloseMobile}
          className="md:hidden text-slate-600 hover:text-slate-900 shrink-0"
          aria-label="Close menu"
        >
          <CloseIcon size={20} />
        </button>
      </div>

      <nav className="flex-1 py-3 space-y-1 min-w-0 overflow-x-hidden overflow-y-auto">
        {mainItems.map((item) => {
          const Icon: any = item.icon;
          const isActive = currentActiveKey === item.key;

          return (
            <button
              key={item.key}
              onClick={() => handleItemClick(item)}
              className={[
                'w-full flex items-center gap-3 px-3 py-2 rounded-md transition text-sm text-slate-700',
                'hover:bg-slate-50',
                'min-w-0',
                iconOnly ? 'justify-center' : '',
                isActive ? 'bg-[#E6F0F7] border-l-4 border-[#0A6AAC] pl-2' : '',
              ].join(' ')}
              title={iconOnly ? item.label : undefined}
            >
              <Icon size={18} className="shrink-0" />
              {!iconOnly && (
                <span className="flex-1 text-left truncate whitespace-nowrap min-w-0">
                  {item.label}
                </span>
              )}
            </button>
          );
        })}

        {adminItems.length > 0 && (
          <div className="mt-4 min-w-0">
            <button
              onClick={() => setAdminOpen((p) => !p)}
              className={[
                'w-full flex items-center justify-between px-3 py-2 text-sm text-slate-600',
                'hover:bg-slate-50 rounded-md',
                'min-w-0',
                iconOnly ? 'justify-center' : '',
              ].join(' ')}
            >
              {!iconOnly && <span className="truncate min-w-0">Admin Settings</span>}
              <FiChevronDown className={adminOpen ? 'rotate-180' : ''} />
            </button>

            {adminOpen &&
              adminItems.map((item) => {
                const Icon: any = item.icon;
                const isActive = currentActiveKey === item.key;

                return (
                  <button
                    key={item.key}
                    onClick={() => handleItemClick(item)}
                    className={[
                      'w-full flex items-center gap-3 py-2 rounded-md transition text-sm text-slate-700',
                      'hover:bg-slate-50',
                      'min-w-0',
                      iconOnly ? 'justify-center px-3' : 'px-6',
                      isActive ? 'bg-[#E6F0F7] border-l-4 border-[#0A6AAC] pl-5' : '',
                    ].join(' ')}
                    title={iconOnly ? item.label : undefined}
                  >
                    <Icon size={16} className="shrink-0" />
                    {!iconOnly && (
                      <span className="flex-1 text-left truncate whitespace-nowrap min-w-0">
                        {item.label}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        )}
      </nav>
      </aside>
    </>
  );
};
